/**
 * Pure helpers for the sign-in page (#387). UI only: the server still decides
 * whether a code is valid, and gives one answer for every failure.
 */

/** How long a parent code lasts (matches CODE_TTL_MS in services/parentAuth.ts). */
export const CODE_TTL_MS = 10 * 60 * 1000;
/** Wait before "Send me a new code" is offered again. */
export const RESEND_COOLDOWN_S = 30;

export type SignInAudience = 'choose' | 'parent' | 'staff';

/** Which path /login opens on. Parent links (?for=parent or a /parent next) open the parent path. */
export function initialAudience(search: string): SignInAudience {
  const params = new URLSearchParams(search);
  const forParam = params.get('for');
  if (forParam === 'parent') return 'parent';
  if (forParam === 'staff') return 'staff';
  if ((params.get('next') ?? '').startsWith('/parent')) return 'parent';
  return 'choose';
}

/** Keeps digits only, at most 6 — so pasting "123 456" or "Code: 123456" works. */
export function normaliseCode(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 6);
}

/**
 * Plain-English message for a failed code check. The server returns one message for
 * wrong, used and expired codes; we only add "expired" when the clock says so.
 */
export function codeErrorMessage(status: number, sentAt: number | null, now: number, serverMessage?: string): string {
  if (status === 429) return serverMessage || 'Too many attempts. Please wait 15 minutes and try again.';
  if (status === 0) return serverMessage || 'We could not reach the school. Check your internet connection and try again.';
  if (status === 400) {
    if (sentAt !== null && now - sentAt >= CODE_TTL_MS) {
      return 'That code has expired. Codes last 10 minutes. Tap "Send me a new code" below.';
    }
    return "That code didn't work. Check the 6 digits in your latest email, or send yourself a new code.";
  }
  return serverMessage || 'Something went wrong. Please try again.';
}
